import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../../hooks/useCalendar';
import { Availability } from '../../../types';
import { persistSlotUpdates } from '../../../utils/persistSlots';
import { useSlotDrag, DragMode, SlotKey } from '../../../hooks/useSlotDrag';
import { useIsDarkMode } from '../../../hooks/useDarkMode';
import { useHourFormat } from '../../../hooks/useHourFormat';
import { getHeatColor } from '../../../utils/heatmap';
import { formatHourLabel, formatSlotAriaLabel, formatWeekdayLong } from '../../../utils/locale';
import { isSlotPast } from '../../../utils/slotTime';
import { PastSlotOverlay } from '../PastSlotOverlay';
import { SlotPopover } from '../SlotPopover';
import { SlotYouMark } from '../SlotYouMark';
import { SlotCountBadge } from '../SlotCountBadge';
import { useSlotHoverPopover } from '../../../hooks/useSlotHoverPopover';
import { useVerticalScrollChaining } from '../../../hooks/useVerticalScrollChaining';

interface WeekViewProps {
  roomId: string;
  participantName: string;
  availability: Availability[];
  weekStartDate: Date;
  visibleDayCount?: number;
  browserFingerprint: string;
  onDayHeaderClick?: (dayDate: string) => void;
  onDrawModeChange?: (active: boolean) => void;
  onNameReassigned?: (name: string) => void;
  onRateLimited?: () => void;
  totalParticipants?: number;
}

function todayKey(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

export function WeekView({
  roomId,
  participantName,
  availability,
  weekStartDate,
  visibleDayCount = 7,
  browserFingerprint,
  onDayHeaderClick,
  onDrawModeChange,
  onNameReassigned,
  onRateLimited,
  totalParticipants,
}: WeekViewProps) {
  const { t, i18n } = useTranslation();
  const { hourFormat } = useHourFormat();
  const [isMobileView, setIsMobileView] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useVerticalScrollChaining(scrollContainerRef);
  const currentHourRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    const checkMobileView = () => setIsMobileView(window.innerWidth <= 500);
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const { timeSlots, setSlotsAvailability, isSlotSelected, getSlotParticipants } = useCalendar(
    availability,
    participantName,
    weekStartDate,
    visibleDayCount
  );

  const displayDays = timeSlots.days;
  const tKey = todayKey();
  const now = new Date();

  const maxCount = useMemo(() => {
    let max = 0;
    displayDays.forEach(day => {
      timeSlots.hours.forEach(h => {
        const c = getSlotParticipants(day.date, h.hour).length;
        if (c > max) max = c;
      });
    });
    return max;
  }, [displayDays, timeSlots.hours, availability, getSlotParticipants]);

  const dark = useIsDarkMode();

  useEffect(() => {
    if (!hasScrolledRef.current && scrollContainerRef.current && currentHourRef.current) {
      setTimeout(() => {
        const container = scrollContainerRef.current;
        const currentElement = currentHourRef.current;
        if (container && currentElement) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = currentElement.getBoundingClientRect().top;
          container.scrollTop = container.scrollTop + (elementTop - containerTop - 100);
          hasScrolledRef.current = true;
        }
      }, 60);
    }
  }, [timeSlots]);

  const commitSlots = useCallback(
    async (mode: DragMode, slots: SlotKey[], phase: 'preview' | 'persist' = 'persist') => {
      const available = mode === 'paint';
      const eligible = slots.filter(s => !isSlotPast(s.day, s.hour));
      if (eligible.length === 0) return;

      if (phase === 'preview') {
        const toChange = eligible.filter(s => isSlotSelected(s.day, s.hour) !== available);
        if (toChange.length === 0) return;
        setSlotsAvailability(toChange, available);
        return;
      }

      const isSlotOnServer = (day: string, hour: number) =>
        availability.some(
          a =>
            a.participant === participantName &&
            a.day === day &&
            a.hour === hour &&
            a.available
        );

      const toChange = eligible.filter(s => isSlotOnServer(s.day, s.hour) !== available);
      if (toChange.length === 0) return;
      setSlotsAvailability(toChange, available);
      const { failed, rateLimited } = await persistSlotUpdates({
        roomId,
        participantName,
        browserFingerprint,
        slots: toChange,
        available,
        onNameReassigned,
      });
      if (rateLimited) onRateLimited?.();
      if (failed.length) setSlotsAvailability(failed, !available);
    },
    [
      availability,
      browserFingerprint,
      isSlotSelected,
      onNameReassigned,
      onRateLimited,
      participantName,
      roomId,
      setSlotsAvailability,
    ]
  );

  const { handlers: dragHandlers, isDrawMode } = useSlotDrag({
    isSelected: isSlotSelected,
    onCommit: commitSlots,
    isSlotDisabled: isSlotPast,
    activation: isMobileView ? 'longPress' : 'immediate',
    onDrawModeChange,
  });

  const {
    popover,
    setPopover,
    openPopoverFor,
    handleSlotMouseEnter,
    handleSlotMouseMove,
    handleSlotMouseLeave,
    handleCalendarMouseLeave,
  } = useSlotHoverPopover(
    isMobileView,
    useCallback(
      (day, hour) => getSlotParticipants(day, hour).length > 0,
      [getSlotParticipants]
    )
  );

  const nCols = displayDays.length;
  const gridTemplate = `48px repeat(${nCols}, minmax(0, 1fr))`;

  const popoverParticipants = popover
    ? getSlotParticipants(popover.day, popover.hour)
    : [];

  const popoverTitle = popover
    ? (() => {
        const [yy, mm, dd] = popover.day.split('-').map(Number);
        const d = new Date(yy, mm - 1, dd);
        const wd = formatWeekdayLong(d, i18n.language);
        return `${wd}, ${formatHourLabel(popover.hour, i18n.language, hourFormat)}`;
      })()
    : '';

  return (
    <div className="w-full min-w-0">
      <div className="overflow-x-auto">
        <div
          className="min-w-0 overflow-hidden rounded-xl border border-room-border"
          style={{ minWidth: 280, boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.04)' }}
          onMouseLeave={handleCalendarMouseLeave}
        >
          <div
            className="grid border-b border-room-border bg-room-surface-2"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <div className="min-w-[48px]" />
            {displayDays.map(day => {
              const isT = day.date === tKey;
              return (
                <button
                  key={day.date}
                  type="button"
                  data-zoom-date={day.date}
                  onClick={() => onDayHeaderClick?.(day.date)}
                  className="border-l border-room-border bg-transparent px-1 py-1.5 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent"
                >
                  <div
                    className={clsx(
                      'text-[8px] font-normal uppercase tracking-widest',
                      isT ? 'text-room-accent' : 'text-room-text-faint'
                    )}
                  >
                    {day.name}
                  </div>
                  <div
                    className={clsx(
                      'mx-auto mt-0.5 flex h-[30px] w-[30px] items-center justify-center rounded-full text-lg font-semibold',
                      isT ? 'today-date-mark text-sm' : 'text-room-text'
                    )}
                  >
                    {new Date(day.date).getDate()}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            ref={scrollContainerRef}
            data-week-scroll
            className={clsx(
              'calendar-scroll-region relative max-h-[400px] transition-shadow duration-150',
              isDrawMode && 'ring-2 ring-inset ring-room-accent/40'
            )}
            style={{ touchAction: 'pan-y' }}
          >
            {isDrawMode ? (
              <div className="pointer-events-none absolute right-1.5 top-1.5 z-10 flex items-center gap-1 rounded-full bg-room-surface/90 px-2 py-0.5 text-[10px] font-medium text-room-accent shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-room-accent" />
                {t('calendar.drawMode')}
              </div>
            ) : null}
            <div
              className="grid"
              style={{
                gridTemplateColumns: gridTemplate,
                touchAction: isDrawMode ? 'none' : undefined,
              }}
              onPointerDown={dragHandlers.onPointerDown}
              onPointerMove={dragHandlers.onPointerMove}
              onPointerUp={dragHandlers.onPointerUp}
              onPointerCancel={dragHandlers.onPointerCancel}
            >
              {timeSlots.hours.map(hour => {
                const isCurrentHour = hour.hour === now.getHours();
                return (
                  <div key={hour.hour} className="contents">
                    <div
                      ref={isCurrentHour ? currentHourRef : null}
                      className="h-[44px] border-b border-r border-room-border py-1 pr-2 text-right text-[9px] text-room-text-dim"
                    >
                      {hour.hour > 0 ? hour.label : null}
                    </div>
                    {displayDays.map(day => {
                      const participants = getSlotParticipants(day.date, hour.hour);
                      const count = participants.length;
                      const selected = isSlotSelected(day.date, hour.hour);
                      const isPast = isSlotPast(day.date, hour.hour);
                      const isTodayCol = day.date === tKey;
                      const showNowLine = isTodayCol && isCurrentHour;
                      const pct = showNowLine ? (now.getMinutes() / 60) * 100 : 0;
                      const heat = getHeatColor(count, Math.max(maxCount, 1), dark);
                      const ariaLabel = formatSlotAriaLabel({
                        day: day.date,
                        hour: hour.hour,
                        count,
                        total: totalParticipants,
                        lang: i18n.language,
                        hourFormat,
                        t,
                      });

                      return (
                        <button
                          key={`${day.date}-${hour.hour}`}
                          type="button"
                          disabled={isPast}
                          data-zoom-date={day.date}
                          data-slot-day={day.date}
                          data-slot-hour={hour.hour}
                          aria-pressed={selected}
                          aria-disabled={isPast}
                          aria-label={ariaLabel}
                          onMouseEnter={e => handleSlotMouseEnter(e, day.date, hour.hour)}
                          onMouseMove={e => handleSlotMouseMove(e, day.date, hour.hour)}
                          onMouseLeave={() => handleSlotMouseLeave(day.date, hour.hour)}
                          className={clsx(
                            'relative h-[44px] border-b border-l border-room-border p-0.5 text-left transition-colors',
                            isPast
                              ? 'cursor-not-allowed'
                              : 'hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-room-accent',
                            isTodayCol && count === 0 && !isPast && 'today-col-slot'
                          )}
                          style={{
                            backgroundColor: count > 0 ? heat : undefined,
                            touchAction: isDrawMode ? 'none' : isMobileView ? 'pan-y' : undefined,
                          }}
                        >
                          {isPast ? <PastSlotOverlay /> : null}
                          {showNowLine ? (
                            <div
                              className="pointer-events-none absolute left-0 right-0 z-[5]"
                              style={{ top: `${pct}%` }}
                            >
                              <div className="relative h-[1.5px] bg-[#E24B4A]">
                                <div className="absolute -left-[3.5px] -top-[2.5px] h-[7px] w-[7px] rounded-full bg-[#E24B4A]" />
                              </div>
                            </div>
                          ) : null}
                          {selected ? <SlotYouMark hasHeat={count > 0} /> : null}
                          <SlotCountBadge
                            count={count}
                            onTap={
                              isMobileView && count > 0
                                ? e => {
                                    const slotEl = (e.currentTarget as HTMLElement).closest<HTMLElement>(
                                      '[data-slot-day][data-slot-hour]'
                                    );
                                    if (!slotEl) return;
                                    openPopoverFor(slotEl, day.date, hour.hour, {
                                      x: e.clientX,
                                      y: e.clientY,
                                    });
                                  }
                                : undefined
                            }
                          />
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {popover ? (
        <SlotPopover
          anchor={popover.rect}
          cursor={popover.cursor}
          title={popoverTitle}
          subtitle={t('calendar.slot.available', { count: popoverParticipants.length })}
          participants={popoverParticipants}
          currentUser={participantName}
          isMobile={isMobileView}
          onClose={() => setPopover(null)}
          onToggle={
            isMobileView
              ? () => {
                  const wasSelected = isSlotSelected(popover.day, popover.hour);
                  commitSlots(wasSelected ? 'clear' : 'paint', [
                    { day: popover.day, hour: popover.hour },
                  ]);
                }
              : undefined
          }
          toggleLabel={t('calendar.slot.toggleAvailability')}
        />
      ) : null}
    </div>
  );
}
